export const en = {
  // Global
  lang: "en" as const,
  siteName: "AVPS – Emergency First Aid Volunteers Association",
  emergency24: "Emergency 24/7 · Office Mon–Fri 9am–1pm",

  // Header nav
  nav: {
    chiSiamo: "About us",
    storia: "Our history",
    organizzazione: "Organization",
    noiEAnpas: "AVPS & ANPAS",
    trasparenza: "Transparency",
    nostriMezzi: "Our vehicles",
    servizi: "Services for the community",
    trasportoSanitario: "Medical transport",
    teleassistenza: "Telecare",
    assistenzaEventi: "Event coverage",
    corsiFormazione: "Training courses",
    continuitaAssistenziale: "After-hours care",
    servizioCivile: "Civil service",
    emergenza112: "Emergency 112",
    iniziative: "Initiatives",
    stellaAmbulanza: "A star in the ambulance",
    progettiAssociazioni: "Partnership projects",
    newsEventi: "News & events",
    news: "News",
    eventi: "Events",
    contatti: "Contact",
    leSedi: "Locations",
    lavoraConNoi: "Work with us",
    sostienici: "Support us",
    donazioni5x: "Donations",
    diventaAmico: "Become a friend of AVPS",
    diventaVolontario: "Become a volunteer",
    areaRiservata: "Member area",
  },

  // Footer
  footer: {
    desc: "Emergency First Aid Volunteers Association of Vimercate. Serving the community with passion and expertise since 1973.",
    linkRapidi: "Quick links",
    nuoviCorsi: "✦ New courses starting",
    diventaVol: "✦ Become a volunteer",
    serviziTitle: "Services",
    contattiTitle: "Contact",
    telefono: "Phone",
    serviziSecondari: "Secondary services: Mon–Fri 9:00am–1:00pm",
    emergenzaSoccorso: "Emergency & rescue: 24/7",
    copyright: "© {year} AVPS – Emergency First Aid Volunteers Association Vimercate. All rights reserved.",
    privacy: "Privacy",
    cookie: "Cookies",
    creditVolontari: "Website created and maintained by AVPS volunteers",
  },

  // Hero slider
  heroSlider: {
    slides: [
      {
        title: "AVPS Vimercate: first responders by your side every day",
        subtitle: "Since 1973, AVPS has served the community: emergency medical services, scheduled transport, telecare and first aid training.",
      },
      {
        title: "Emergency medical rescue in Vimercate, 24 hours a day",
        subtitle: "Our first responders provide an emergency service, managed by Areu, in the area of competence.",
      },
      {
        title: "Ambulances and medical transport for the Vimercate area",
        subtitle: "Equipped ambulances, basic life support vehicles and medical transport available for those in need.",
      },
    ],
    cta1: "Become a volunteer",
    cta3: "Support us",
    prevSlide: "Previous slide",
    nextSlide: "Next slide",
    goToSlide: "Go to slide {n}",
  },

  // Index page
  index: {
    servicesLabel: "Services for the community",
    servicesTitle: "How we can help",
    servicesSubtitle: "The services we offer to the community, every day.",
    services: [
      {
        title: "Emergency medical service 112",
        desc: "Emergency interventions with qualified crews and equipped vehicles, coordinated with AREU 112.",
        cta: "In an emergency call 112",
      },
      {
        title: "Medical transport",
        desc: "Hospital admissions, discharges, specialist visits and exams: a punctual and comfortable service.",
        cta: "Book at 039 66 12 112",
      },
      {
        title: "Telecare",
        desc: "24/7 SOS service, with a weekly friendly call for vulnerable people.",
        cta: "Info: segreteria@avps.it",
      },
      {
        title: "Events & public gatherings",
        desc: "Medical coverage with first responders and ambulances for sporting, cultural and public events.",
        cta: "Request medical coverage",
      },
      {
        title: "Training courses",
        desc: "First aid, BLS-D, paediatric choking. For citizens, companies and schools.",
        cta: "Write to segreteria@avps.it",
      },
    ],
    learnMore: "View service details",
    historyLabel: "Our history",
    historyTitle: "Over 50 years of commitment",
    historyText: "Born in 1973 from the dream of five founders, AVPS has grown alongside its community, keeping the same enthusiasm and continuous training. Today we count on hundreds of volunteers, dozens of vehicles and a commitment that has never stopped.",
    historyBtn: "Discover our history",
    stats: [
      { value: "400+", label: "Active volunteers" },
      { value: "50+", label: "Years of service" },
      { value: "30+", label: "Operational vehicles" },
      { value: "24/7", label: "Always on duty" },
    ],
    since: "Since 1973",
    fivexTitle: "Donate your 5×1000 to AVPS",
    fivexText: "It costs you nothing: enter our tax code in your tax return.",
    fivexCf: "Tax Code",
  },

  // CTA section
  cta: {
    title: "Make a difference, become a volunteer",
    text: "Joining AVPS means being part of a family that commits every day to helping others. We train you, support you, and help you grow.",
    btn1: "Become a volunteer",
    btn2: "Support us",
  },

  // Contact form
  form: {
    name: "Full name *",
    namePlaceholder: "Your name",
    email: "Email *",
    emailPlaceholder: "Your email",
    phone: "Phone",
    phonePlaceholder: "Your number (optional)",
    message: "Message *",
    messagePlaceholder: "How can we help you?",
    privacy: "I consent to the processing of personal data in accordance with GDPR. *",
    privacyNote: "By continuing, you confirm that you have read the privacy and cookie policies and authorize the processing of your data solely to manage your request.",
    privacyError: "You must accept the privacy policy to proceed.",
    sending: "Sending...",
    success: "Message sent successfully! We will get back to you shortly.",
    error: "An error occurred. Please try again or contact us directly.",
    submit: "Send message",
    nameRequired: "Name is required",
    emailInvalid: "Invalid email address",
    messageRequired: "Message is required",
  },

  // Chi siamo
  chiSiamo: {
    title: "About us",
    subtitle: "AVPS – Emergency First Aid Volunteers Association of Vimercate. Serving the community with passion, expertise and dedication since 1973.",
    intro: "AVPS was born from the will of five founders to provide an emergency first aid service to the Vimercate area. Today we are a well-established organization, with hundreds of trained volunteers, a modern operational headquarters and a cutting-edge fleet of vehicles. Our daily commitment translates into emergency medical services, scheduled transports, telecare, training and much more.",
    cards: [
      { title: "Our history", desc: "Serving the community of Vimercate and its surroundings since 1973." },
      { title: "Organization", desc: "Governing bodies, volunteers and employees who make our work possible." },
      { title: "AVPS & ANPAS", desc: "Part of the ANPAS national network, Italy's largest health volunteering organization." },
      { title: "Our vehicles", desc: "A modern and equipped fleet for every type of intervention." },
    ],
    learnMore: "Discover this section",
  },

  // Storia
  storia: {
    title: "Our history",
    subtitle: "AVPS was born from the dream of 5 founders, shared with an entire community.",
    intro: "For over 50 years we have dedicated our efforts to serving the population, every day with the same enthusiasm. AVPS was born in 1973 from a project of 5 people who wanted to offer the community an adequate emergency rescue service.",
    timelineTitle: "The history",
    timeline: [
      { year: "1973", text: "AVPS is founded by 5 people: Antonio Aggrazia, Omero Castiglioni, Ciro Gasperoni, Umberto Marchesi and Walter Verderio. First first-aid courses begin." },
      { year: "1974", text: "AVPS signs its first agreement with the Vimercate Hospital. On September 16, volunteers do their first emergency shift." },
      { year: "1975", text: "The first ambulance, \"Pinellina\", is purchased. Thanks to a fundraiser, a second ambulance follows." },
      { year: "1976–1980", text: "Earthquakes hit Friuli and Irpinia. AVPS volunteers join the rescue efforts, creating the foundation for the Civil Protection Department from 1985." },
      { year: "1980s", text: "Generational change in the Board. The Lombardy Region officially recognizes the Association." },
      { year: "1994", text: "AVPS launches the home Telecare service for elderly or isolated people in the Vimercate area." },
      { year: "1996", text: "118 becomes the single emergency number in Lombardy. Operations expand to all-day coverage. The Association hires its first six employees. New services are born." },
      { year: "1999", text: "The Prontofarmacia service is born: Telecare volunteers provide citizens with information about open pharmacies." },
      { year: "2001", text: "On September 23, the new headquarters at Via Cadorna 28 in Vimercate is inaugurated." },
      { year: "2003", text: "AVPS begins managing the After-Hours Care service. Emergency service is extended to Agrate Brianza with a 24h station." },
      { year: "2010", text: "AVPS creates a training programme for children, going into schools as first aid teachers." },
      { year: "2011", text: "A blood collection centre opens at the Ruginello Municipal Pharmacy, managed by AVPS volunteers." },
      { year: "2017", text: "The association adopts a CODE OF ETHICS and creates/updates various operational regulations." },
      { year: "2020", text: "During the COVID pandemic, AVPS keeps all services running and adds home delivery of medicines and groceries." },
      { year: "2021–2023", text: "3 LUCAS devices (semi-automatic external massagers) are donated to improve cardiac arrest interventions." },
    ],
    interventiTitle: "Extraordinary interventions",
    interventi: [
      { year: "1976", event: "Friuli earthquake", detail: "Continuous presence from May to September" },
      { year: "1980", event: "Irpinia earthquake", detail: "25 days on site with 6 people in weekly shifts" },
      { year: "1987", event: "Valtellina flood", detail: "One week on site with 6 people" },
      { year: "1987", event: "ATR42 crash in Barni (CO)", detail: "2 days on site with 11 people" },
      { year: "1987", event: "Building explosion in Lecco", detail: "One day on site with 8 people" },
      { year: "1993", event: "Genoa flood", detail: "4 days on site with 3 people" },
      { year: "1994", event: "Piedmont flood", detail: "6 days on site with 120 students participating" },
      { year: "1997", event: "Umbria and Marche earthquake", detail: "16 days on site with shifts of 6 people, managing 6 base camps near Nocera Umbra" },
      { year: "2020", event: "COVID pandemic", detail: "All services maintained, plus home delivery of medicines, groceries and food packages for families in need" },
    ],
    awardsTitle: "Awards and honours",
    awards: [
      "ROTARY Foundation of Rotary International honour: \"Foundation members appreciate your selfless commitment and generous help.\"",
      "City of Vimercate – 2020: \"Silver Medal for Civil Merit\"",
      "Municipality of Burago di Molgora – September 13, 2020: \"A Diamond for Life\"",
      "Municipality of Concorezzo – 2023: \"Medal for Civil Valor\"",
    ],
  },

  // Organizzazione
  organizzazione: {
    title: "Governing bodies",
    subtitle: "The governing bodies were defined at the assembly on 04.06.24, following the elections held on 25 and 26.05.24.",
    consiglio: "Board of Directors",
    revisori: "Auditors",
    probiviri: "Board of Arbitrators",
    contattiConsiglio: "To contact all Board members write to:",
    contattiRevisori: "To contact all Auditors write to:",
    contattiProbiviri: "To contact all Arbitrators write to:",
  },

  // Noi e ANPAS
  noiEAnpas: {
    title: "AVPS & ANPAS",
    subtitle: "AVPS is part of ANPAS, Italy's largest national health volunteering organization, with over 100,000 volunteers throughout the country.",
    p1: "ANPAS – National Association of Public Assistance – is the largest health volunteering organization in Italy. With over 900 affiliated associations and more than 100,000 volunteers, ANPAS is a point of reference for rescue and assistance across the entire country.",
    p2: "AVPS is proudly part of this network, sharing its values of solidarity, continuous training and community service. Through ANPAS, we participate in national projects, civil protection emergencies and awareness campaigns.",
  },

  // Nostri mezzi
  nostriMezzi: {
    title: "Our vehicles",
    subtitle: "A modern and equipped fleet to ensure rapid, safe and professional interventions.",
    text: "AVPS has a fleet of over 30 vehicles, including advanced rescue ambulances, scheduled medical transport vehicles and support vehicles. Every vehicle is equipped with the latest medical equipment and undergoes regular maintenance to ensure maximum safety.",
    altriMezzi: "Other vehicles",
    ambulanzeLabel: "Ambulances & medical vehicles",
    veicoliSupportoLabel: "Support vehicles",
    parcoDiServizio: "Our service fleet",
    gippone: "LAND ROVER GIPPONE E + UTES",
  },

  // Trasparenza
  trasparenza: {
    title: "Transparency",
    subtitle: "Public contributions received by AVPS, published in compliance with current regulations.",
    text: "In compliance with Italian Law no. 124 of August 4, 2017 and subsequent amendments, AVPS publishes information about public contributions received.",
    percheDonare: "Why your support matters",
    percheDonareText: "AVPS operates thanks to an agreement with AREU (Regional Emergency Agency), which covers part of the costs of the emergency medical service. However, this agreement alone is not enough to sustain all the solidarity activities we carry out daily: telecare, school training, event coverage, vehicle maintenance and purchase of life-saving equipment. To continue providing these services to the community, we need everyone's support.",
    contributi: "Public contributions",
    documentoPdf: "PDF document",
  },

  // Servizi
  servizi: {
    title: "Services for the community",
    subtitle: "A range of health and social services for the community, every day of the year.",
    learnMore: "View service details",
    items: [
      { title: "Emergency medical service 112", desc: "Emergency interventions with qualified crews, coordinated with AREU." },
      { title: "Scheduled medical transport", desc: "Hospital admissions, discharges, visits, exams: a punctual and safe service." },
      { title: "Telecare", desc: "24/7 SOS with weekly friendly call." },
      { title: "Event coverage", desc: "Professional medical coverage for events and public gatherings." },
      { title: "Training courses", desc: "First aid, BLS-D, paediatric choking for everyone." },
      { title: "After-hours care", desc: "Medical care for non-urgent needs during evening, night and holiday hours." },
      { title: "Civil service", desc: "A 12-month formative experience serving the community." },
    ],
  },

  // Emergenza 112
  emergenza: {
    title: "Emergency medical service 112",
    subtitle: "Our commitment to emergency care, in coordination with AREU and the 112 system of Lombardy Region.",
    intro: "AVPS operates within the regional emergency medical system, with trained crews and equipped vehicles ready to respond 24 hours a day. Our volunteers and employees respond to calls dispatched by the AREU 112 Operations Centre, ensuring rapid and professional interventions across the entire Vimercate area.",
    personaleTitle: "Qualified personnel",
    personaleDesc: "Our crews are composed of highly trained first responders, constantly updated through ongoing training and continuous professional development.",
    lucasTitle: "LUCAS technology",
    lucasDesc: "Our ambulances are equipped with the LUCAS mechanical chest compression device: an automatic system that ensures consistent, high-quality cardiac massage, even during transport.",
    numero112Title: "Single number: 112",
    numero112Desc: "The single emergency number is 112, active 24 hours a day, 7 days a week, throughout the region.",
    lucasDetailTitle: "Technological excellence: the LUCAS device",
    lucasDetailP1: "The LUCAS (Lund University Cardiac Assist System) is a mechanical chest compression device applied to patients in cardiac arrest. Unlike manual massage, LUCAS delivers consistent compressions at the correct depth and rate, without interruption — even during transport manoeuvres or in confined spaces.",
    lucasDetailP2: "This device allows our first responders to focus on other life-saving procedures, significantly improving the overall quality of rescue and the patient's chances of survival.",
    operativi247: "Operational 24/7",
    operativi247Desc: "Our crews cover emergency shifts around the clock, ensuring presence on the territory at all times.",
    formazioneContinua: "Continuous training",
    formazioneContinuaDesc: "Every first responder follows a rigorous training programme and is constantly updated on the latest guidelines and protocols.",
    alertEmergenza: "In case of a medical emergency, call 112 immediately.",
    alertEmergenzaSub: "Do not come to our headquarters: the operations centre will dispatch the nearest and most suitable vehicle.",
    infoTitle: "Need information?",
    infoDesc: "For questions about the emergency service or the training of our crews, write to us.",
  },

  // Trasporto sanitario
  trasporto: {
    title: "Scheduled medical transport",
    subtitle: "This service is dedicated to patients unable to make healthcare-related transfers on their own.",
    intro: "AVPS volunteers are available for ambulance or other suitable vehicle transport for patients who need to visit healthcare facilities for specialist appointments, exams or treatments, or for transfers between facilities (hospital to hospital or care facility) and discharges (hospital to home).",
    accompagni: "Simple accompaniments for specialist exams or medical visits are also available.",
    comePrenotare: "How to book",
    prenotazioneText: "The service is available by appointment only by calling our switchboard at 039/6612112. A volunteer will take care of your request.",
    centralino: "Switchboard",
    orariPrenotazione: "Service hours",
    orariLunVen: "Mon–Fri: 9:00am–1:00pm and 2:00pm–6:00pm",
    orariSab: "Saturday: morning",
  },

  // Teleassistenza
  teleassistenza: {
    title: "Telecare service",
    subtitle: "Our Telecare volunteers provide a service dedicated to elderly, isolated or seriously ill people.",
    intro: "Telecare is a modern remote support system: through a remote control connected to a special terminal (SOS) installed at their home, the assisted person can contact AVPS volunteers at any time at their Vimercate headquarters (the project is carried out in collaboration with local municipalities and Offerta Sociale).",
    attivo247: "The service is active 24 hours a day, 365 days a year.",
    attivitaTitle: "Activities carried out by our volunteers",
    attivitaItems: [
      "A weekly friendly call with each assisted person, at a set time",
      "Coordinating rescue in case of domestic accidents, illness, accidental falls…",
    ],
    chiamataAmichevoleTitle: "The friendly call",
    chiamataAmichevoleText: "On a weekly basis, AVPS volunteers contact their assisted persons to check on their health and needs, comfort them, encourage them or simply keep them company. Volunteers are properly trained through a course held by AVPS instructors.",
    chiamataAmichevoleText2: "The call is always made by the same volunteer (since volunteer shifts are fixed, as is the weekly appointment agreed with the family). This way the assisted person has a known, trusted contact.",
    chiamataAmichevoleText3: "The friendly call also serves to test device efficiency. It is essential that Telecare users, especially the elderly, can correctly activate the emergency call when needed.",
    chiamataAmichevoleText4: "Thanks to the volunteers, the elderly get used to always carrying their remote control and using it correctly, call after call.",
    chiamataEmergenzaTitle: "The emergency call",
    chiamataEmergenzaText: "The assisted person can contact AVPS operators at any time through a radio-command worn around the neck or kept in a pocket, activated by simply pressing a button.",
    chiamataEmergenzaText2: "The alarm is sent to the Association's digital receiver, displaying the emergency screen with the user's individual file. The operator then immediately calls the assisted person on the speakerphone installed by AVPS.",
    chiamataEmergenzaText3: "If the person responds, the operator promptly activates the most appropriate intervention. If they don't respond, the operator calls the first registered family member or friend and works until the issue is resolved.",
    comeAttivare: "How to activate the Telecare service",
    comeAttivareText: "To activate the service, you must contact the Social Services office of the assisted person's municipality of residence. The municipalities using the Telecare service are those belonging to Offerta Sociale.",
    comuniCoperti: "Municipalities covered",
    comuni: ["Agrate Brianza","Aicurzio","Arcore","Basiano","Bellusco","Bernareggio","Burago Molgora","Busnago","Camparada","Caponago","Carnate","Cavenago Brianza","Concorezzo","Cornate d'Adda","Correzzana","Grezzago","Lesmo","Masate","Mezzago","Ornago","Pozzo d'Adda","Roncello","Ronco Briantino","Sulbiate","Trezzano Rosa","Trezzo sull'Adda","Usmate Velate","Vaprio d'Adda","Vimercate"],
    infoEmail: "For information: segreteria@avps.it",
  },

  // Assistenza eventi
  assistenzaEventi: {
    title: "Event and public gathering coverage",
    subtitle: "Do you need medical assistance for a public event or gathering?",
    intro: "AVPS provides medical assistance during events organized in our territory (especially those attracting a significant number of people) through crews of volunteer first responders on foot or, if the event requires it, with one or more ambulances.",
    normativa: "On October 7, 2014, Lombardy Region (DGR no. 2453) introduced new safety regulations with a mandatory procedure for all organizers.",
    tempiAnticipo: "Please note that the procedure must be prepared in advance depending on the risk level (15 days for low risk, 30 days for medium, 45 days for high).",
    proceduraTitle: "Procedure",
    steps: [
      "Go to the G.A.M.E.S. platform and register.",
      "Begin the guided registration on the G.A.M.E.S. platform.",
      "Answer all questions on the platform to determine if and what type of medical coverage is needed.",
      "Save the draft (do not submit at this stage). It is your responsibility to find an association that can provide the required service.",
      "Call Colombo Noemi at AVPS at +39 335 129 7812 or email servizisportivi@avps.it. Share the platform results (Risk calculation and Maurer Coefficient) and all service details with her.",
      "If the Maurer coefficient indicates no medical coverage is needed, you can still choose to request support — indicate this during registration.",
      "Once agreed and confirmed by A.V.P.S. Vimercate, complete the registration on the G.A.M.E.S. platform: indicate Volontari Vimercate as the medical safety guarantor and submit.",
      "If everything is filled in correctly you will receive an approval email; otherwise an email indicating the error.",
    ],
    contattoOperativo: "Operational contact",
    contattoInfo: "For more information, contact the reference person:",
    richiediCopertura: "Request medical coverage",
    piattaformaGames: "G.A.M.E.S. Platform",
    piattaformaGamesDesc: "Click here to access the G.A.M.E.S. platform and request medical assistance for your event.",
    piattaformaGamesUrl: "https://games.areu.lombardia.it",
  },

  // Corsi formazione
  corsi: {
    title: "Training",
    subtitle: "AVPS confirms its commitment to training for all citizens.",
    intro: "AVPS carries out intensive internal training for its volunteers/employees to maintain effective training so they can carry out their operational role in a conscious, prepared and autonomous way.",
    intro2: "We organize training courses for the entire population, which represents the first link in the rescue chain, because we are convinced that a greater spread of first aid culture is of fundamental importance not only for young people, but for the entire community.",
    argomentiTitle: "Main topics covered in our courses",
    argomenti: [
      "How to alert emergency services via 112",
      "First Aid",
      "Cardiopulmonary Resuscitation including use of Automatic External Defibrillator",
      "Life-saving Paediatric Choking manoeuvres",
    ],
    altriArgomenti: "Other specific topics can be agreed with us.",
    scuoleTitle: "In schools",
    scuoleIntro: "Courses are aimed at students of all levels and are adapted to their different ages. The goal is to create training paths, from kindergarten onwards, aimed at a greater spread of First Aid culture.",
    scuoleItems: [
      { title: "Projects for young children", desc: "Anyone can make a difference in an emergency. Many children have called emergency services saving the lives of parents and grandparents. Our courses aim to make children aware of the correct behaviour in a medical emergency. The AVPS schools project was born in 2007." },
      { title: "Projects for teenagers", desc: "For several years, students have followed training projects in medical rescue at middle and high schools in the Vimercate area. Through highly practical courses with solid theoretical foundations, students learn first aid techniques and CPR with AED." },
      { title: "Paediatric emergency courses", desc: "Through dedicated networks, clinics and parent associations, we address this specific topic for parents, grandparents and anyone in contact with children. We also organize courses for kindergarten teaching staff." },
      { title: "BLS-D lay operator courses", desc: "These courses are open to all citizens. The 5-hour courses provide regional certification for use of the semi-Automatic External Defibrillator, including paediatric treatment and airway obstruction guidelines." },
    ],
    ctaText: "For any information write to:",
    ctaBtn: "segreteria@avps.it",
  },

  // Guardia medica
  guardiaMedica: {
    title: "After-hours care (Continuità Assistenziale)",
    subtitle: "The volunteers of the Guardia Medica group provide an essential service for the community.",
    intro: "The volunteers of the Guardia Medica (After-Hours Care) group carry out the following services:",
    servizi: [
      "Welcoming people at the Casa di Comunità",
      "Accompanying doctors to the patient's home when needed",
    ],
    sede: "Service location",
    sedeIndirizzo: "Casa di Comunità in Vimercate, Via Giuditta Brambilla 11 (opposite Vimercate Hospital)",
    accessoAppuntamento: "Access is by appointment only.",
    coseFareTitle: "What to do if you need help?",
    coseFareText: "Call 116117 for non-urgent medical assistance, services or health advice.",
    coseFareText2: "By calling this number, citizens are connected to a qualified operator who, through a phone assessment, redirects the request to the appropriate service.",
    orari: "Service hours",
    orariFeriali: "Weekdays: from 8:00pm to 8:00am",
    orariPrefestivi: "Holidays and pre-holidays: 24 hours",
    alertEmergenza: "⚠️ For emergency medical rescue and dispatch of emergency vehicles, call 112.",
    richiestaInfo: "Request information",
    richiestaInfoText: "For questions about the After-Hours Care service, write to us.",
    locandinaLabel: "Download the informational flyer",
    avvisoSospensioneTitle: "Important notice",
    avvisoSospensioneText: "Starting from 1 April 2026, the service carried out by AVPS volunteers at the After-Hours Care is suspended. The Guardia Medica service remains active: for non-urgent assistance call 116117.",
  },

  // Servizio civile
  servizioCivile: {
    title: "AVPS and the Universal Civil Service",
    subtitle: "Would you like to participate too?",
    cosETitle: "The Universal Civil Service",
    cosEText: "The Universal Civil Service is an important opportunity for young people to engage in meaningful projects, contributing to community wellbeing and gaining valuable experience.",
    cosEText2: "AVPS, through the ANPAS network, participates in this programme. During their months at AVPS, young people receive training useful for their personal growth and for the services they will perform. They also have the opportunity to experience the many activities and learn about the various realities within the Association.",
    dettagli: "Practical details",
    dettagliItems: [
      "Duration: 12 months",
      "Commitment: 25 hours per week",
      "Monthly allowance: approximately €500",
    ],
    requisitiTitle: "Requirements",
    requisitiItems: [
      "Be between 18 and 28 years of age",
      "Be an Italian citizen",
      "Have no criminal record",
    ],
    bandoInfo: "The 2026 call is open! The deadline to apply has been extended to April 16, 2026 at 2:00 PM. Applications are submitted exclusively online through the Department of Youth Policies portal.",
    infoEmail: "For more information: segreteria@avps.it",
    candidati: "Apply now",
    sitoSCU: "SCU Department website",
    bannerTitle: "Civil Service Call 2026",
    bannerText: "2,569 positions in ANPAS volunteer organizations. 12 months, 25 hours/week, ~€520/month allowance. Aged 18–28? Apply by April 16!",
    bannerDeadline: "Deadline: April 16, 2026 at 2:00 PM",
    bannerDaysLeft: "days left",
    bannerBadge: "Open call",
    bannerCta: "See course details",
    bannerApply: "Apply online",
    bannerInfo: "ANPAS call info",
  },

  // Corso Soccorritore
  corsoSoccorritore: {
    badge: "Registrations open",
    kicker: "Regional Course for",
    title: "Emergency Responder",
    claim: "You could be next: learn, join the team, make a difference!",
    date: "Tuesday, 20 October 2026",
    time: "9:00 PM",
    venue: "Oratorio di Velasca",
    address: "Via E. De Amicis 3/5 — Velasca, Vimercate",
    presentationNote: "Course presentation evening",
    intro: "The course is free and intended for those who wish to serve on emergency and medical transport vehicles.",
    modulesTitle: "Course outline",
    modules: [
      { name: "TS Module", hours: "46 hours", cert: "\"Medical Transport Operator\" certification" },
      { name: "SSE Module", hours: "78 hours", cert: "Regional qualification as \"Soccorritore Esecutore\"" },
    ],
    infoTitle: "Information",
    email: "info@avps.it",
    phone: "039 661 2112",
    ctaInfo: "Request information",
    ctaCall: "Call 039 661 2112",
    ctaMore: "Discover the course",
    daysLeft: "days to the presentation",
    posterAlt: "Poster of the AVPS Regional Emergency Responder Course on 20 October 2026",
  },

  // Diventa volontario
  diventaVolontario: {
    title: "Become a volunteer",
    subtitle: "Joining AVPS means being part of a family that commits every day to helping others. We train you, support you, and help you grow.",
    perche: "Why become a volunteer?",
    reasons: [
      { title: "Help others", desc: "Every day you make a difference in someone's life." },
      { title: "Free training", desc: "We train you: first aid courses, BLS-D and much more." },
      { title: "A family", desc: "You'll join a united, motivated and welcoming group." },
      { title: "Flexibility", desc: "You choose when and how much to dedicate: every contribution counts." },
    ],
    candidatiOra: "Apply now",
    candidatiDesc: "Fill in the form and we will contact you for an introductory meeting.",
    disponibilita: "Availability",
    seleziona: "Select",
    mattina: "Morning",
    pomeriggio: "Afternoon",
    sera: "Evening",
    weekend: "Weekend",
    flessibile: "Flexible",
    noteLabel: "Notes",
    notePlaceholder: "Tell us something about yourself and your motivations...",
    inviaBtn: "Submit application",
  },

  // Iniziative
  iniziative: {
    title: "Initiatives",
    subtitle: "Our special projects and collaborations with other local associations.",
    stellaTitle: "A star in the ambulance",
    stellaDesc: "A special project that combines solidarity and closeness, bringing a smile to those facing a difficult time.",
    progettiTitle: "Partnership projects",
    progettiDesc: "Collaborations with local organizations for solidarity, training and awareness initiatives.",
    learnMore: "Explore this initiative",
  },

  // Stella in ambulanza
  stella: {
    title: "A star in the ambulance",
    subtitle: "Wrapping gifts and distributing Christmas stars to support training and rescue vehicles.",
    whatTitle: "The project",
    whatText: "This initiative's main goal is to wrap Christmas gifts and distribute Christmas poinsettias in exchange for a donation.",
    historyTitle: "The history",
    historyText: "The project started in 1999 with a stall at the Vimercate market and since 2001 has always taken place in the shopping gallery of the \"Torri Bianche\" mall in Vimercate.",
    detailText: "Every year it starts on December 7th and ends on Christmas Eve: our volunteers are present every weekend and generally on several days leading up to Christmas. Our volunteers have become experts at creating beautiful gift wrapping, and our Christmas stars come directly from a local nursery: many people come back to visit us every year!",
    fundsText: "The funds raised are used to invest in first responder training, improving the quality of care and purchasing medical devices for our ambulances.",
    contact: "For more information:",
  },

  // Progetti associazioni
  progetti: {
    title: "Partnership projects",
    subtitle: "AVPS actively collaborates with several local associations.",
    intro: "AVPS actively collaborates with several local associations, here are the projects:",
    items: [
      { title: "Cork and plastic cap collection for FMS", text: "Supporting the Haematology division of Niguarda Hospital – funds research projects, clinical studies, scholarships and laboratory equipment.", hasLetter: true },
      { title: "Coin collection for Mani di Pace ODV", text: "Through the \"CASA KILT\" project, providing hospitality and care for orphaned or abandoned children in Siem Reap, Cambodia. We collect 1, 2 and 5 cent coins.", hasPhoto: true },
      { title: "Clothing collection for Opera San Vincenzo Vimercate", text: "Collection of clothing and household linen (bedding, towels…) to help people in difficulty in our area." },
      { title: "\"Research Azaleas\" for AIRC", text: "AVPS supports AIRC (Italian Cancer Research Association). In May 2025 we organized a stall for the distribution of research azaleas.", hasPhoto: true },
    ],
    piantumazione: "Over the years we have followed several tree planting projects in the municipality of Vimercate with plants from the Ersaf Nursery in Curno.",
    ambulanzaSenegalTitle: "An ambulance for Niakhene",
    ambulanzaSenegalText: "We are thrilled to have contributed by donating our VIME40 ambulance to the \"An ambulance for Niakhene\" project by the ON OFF association. The ambulance has reached its destination at the Ndemene Health Centre in Senegal and has been operational since December 2025! It will be of great help to the local population.",
    referenteTitle: "Information",
    referenteText: "For information contact our coordinator Colombo Davide via email",
    referenteEmail: "davide.colombo@avps.it",
    referenteTel: "338 968 7658",
  },

  // News
  newsPage: {
    title: "News & events",
    subtitle: "The latest news, updates and events from AVPS.",
    news: "News",
    eventi: "Events",
    listHeading: "Latest news from the Association",
    ctaTitle: "Want to make a difference?",
    ctaDesc: "Join our volunteers or support our activities.",
    newsItems: [
      { title: "New ambulance for the Vimercate station", date: "February 15, 2026", category: "Association", excerpt: "A new rescue vehicle joins our fleet." },
      { title: "BLS-D course open to citizens", date: "February 10, 2026", category: "Training", excerpt: "New cycle of BLS-D courses for the public: 5 hours to learn how to save a life." },
      { title: "AVPS alongside the community: 2025 report", date: "January 5, 2026", category: "Association", excerpt: "A year of commitment, numbers and results serving the community." },
    ],
  },

  // Eventi
  eventiPage: {
    title: "Events & activities",
    subtitle: "All events organized and supported by AVPS: training, solidarity, drills and community initiatives.",
    prossimiAppuntamenti: "Upcoming events",
    prossimiDesc: "Don't miss them! Sign up or participate.",
    nessunoEvento: "There are currently no scheduled events. Follow us on social media to stay updated!",
    partecipa: "Want to participate actively?",
    partecipaDesc: "Become an AVPS volunteer: we train you and you make a difference every day.",
    attivitaSvolte: "Past activities",
    attivitaSvolteDesc: "Activities and events we were part of.",
    iscrivitiOra: "Sign up now",
    eventiPassati: [
      { title: "A Star in the Ambulance – Christmas 2024", date: "December 24, 2024", location: "Vimercate Hospital", type: "Solidarity initiative", excerpt: "AVPS Santa Clauses brought gifts and smiles to children hospitalized in the paediatrics ward." },
    ],
  },

  // Contatti
  contattiPage: {
    title: "Contact",
    subtitle: "Need information? We're here for you. Call us, write to us or visit us at our headquarters.",
    scrivici: "Write to us",
    informazioni: "Information",
    telefono: "Phone",
    email: "Email",
    orariUfficio: "Office hours",
    orariValue: "Mon–Fri 9:00am–1:00pm",
    leSedi: "Our locations",
  },

  // Le sedi
  leSediPage: {
    title: "Our locations",
    subtitle: "Four operational locations to best cover the territory.",
  },

  // Lavora con noi
  lavoraConNoi: {
    title: "Work with us",
    subtitle: "We are looking for motivated people to join our rescue team! If you want to work in a dynamic and stimulating environment, send us your CV!",
    posizione: "Open position: Ambulance Driver/Rescuer",
    requisiti: "Requirements",
    requisitiItems: [
      "Executive Rescuer certification",
      "Driver and Team Leader qualifications (preferred)",
      "Willingness to work shifts",
    ],
    offriamo: "What we offer",
    offriamoItems: [
      "Fixed-term contract for the summer period (June–September) with possibility of permanent employment",
      "Ongoing training and professional development",
      "Career growth opportunities",
    ],
    cvUploadLabel: "Attach your CV",
    cvMaxSize: "Max size: 4MB – PDF, DOC, DOCX",
    cvTooLarge: "File exceeds the maximum size of 4MB.",
    cvUploadError: "Error uploading file. Please try again.",
    inviaCandidatura: "Submit your application",
    presentazione: "Cover letter",
    presentazionePh: "Send your CV and a cover letter...",
    inviaBtn: "Submit application",
    alternativa: "Send your CV and cover letter to:",
  },

  // Sostienici
  sostieniciPage: {
    title: "Support AVPS",
    subtitle: "Every day we do our best to serve our community. But our efforts alone are not enough — we need generous people like you.",
    aiutaci: "Help us help you",
    aiutaciText: "Without your help, our work would not be possible. With your support we will be able to purchase new rescue vehicles and cutting-edge equipment, and invest part of it in training courses for our volunteers.",
    comeSostenerci: "You can support us in several ways:",
    dona5x: "Donate your 5×1000",
    dona5xText: "Enter our tax code in the appropriate box on your tax return. It costs you nothing!",
    codiceFiscale: "Tax Code",
    bonifico: "Make a bank transfer",
    bonificoText: "To the account held by AVPS at BCC Milano – Vimercate.",
    bonificoCausale: "Remember to write your address in the transfer description so we can thank you.",
    cards: [
      { title: "Donations", desc: "Donate your 5×1000 to AVPS or make a direct donation. Every contribution helps us improve our services." },
      { title: "Become a friend of AVPS", desc: "With an annual or recurring donation you can support our activities on an ongoing basis." },
    ],
    learnMore: "See how to support us",
  },

  // Donazioni 5x1000
  donazioni: {
    title: "Support AVPS",
    subtitle: "Every day we do our best to serve our community. But our efforts alone are not enough — we need generous people like you.",
    aiutaci: "Help us help you",
    aiutaciText: "Without your help, our work would not be possible. With your support we will be able to purchase new rescue vehicles and cutting-edge equipment, and invest part of it in training courses for our volunteers.",
    dona5x: "Donate your 5×1000",
    dona5xText: "It costs you nothing: enter our tax code in the appropriate box on your tax return (Modello Unico or 730).",
    codiceFiscale: "AVPS Tax Code",
    cfCopiato: "Tax code copied to clipboard",
    bonifico: "Make a bank transfer",
    bonificoText: "You can support AVPS with a bank transfer to the AVPS account at BCC Milano – Vimercate.",
    ibanCopiato: "IBAN copied to clipboard",
    intestatario: "Account holder: Associazione Volontari di Pronto Soccorso e Pubblica Assistenza",
    causale: "Remember to write your address in the transfer description so we can thank you.",
    donaCarta: "Donate by credit card",
    donaCartaText: "Fill in the form below to make a donation. We will contact you to complete the payment.",
    noteLabel: "Donation notes",
    notePlaceholder: "Amount and preferred method...",
    inviaBtn: "Send donation request",
    info: "For information:",
  },

  // Diventa amico
  diventaAmicoPage: {
    title: "Become a friend of AVPS",
    subtitle: "With an annual or recurring donation you can support our activities on an ongoing basis.",
    text: "Becoming a \"Friend of AVPS\" means choosing to support us on a recurring basis, with a small annual donation that allows us to plan and improve our services.",
    comeAderire: "How to join",
    comeAderireText: "To join the \"Friend of AVPS\" programme or to receive information, contact us:",
    ctaBtn: "Write to amici@avps.it",
  },

  // Lascito testamentario
  lascito: {
    title: "Testamentary bequest",
    subtitle: "A gesture of great generosity that leaves a lasting mark on the community.",
    heading: "Leave a mark that lasts over time",
    p1: "A testamentary bequest in favour of AVPS is an act of extraordinary generosity that helps support emergency medical services and community care into the future. It can be a sum of money, movable or immovable property.",
    p2: "A testamentary bequest does not affect the rights of legitimate heirs: it is a way to allocate a portion of your estate to a cause you believe in.",
    info: "For confidential information:",
  },

  // Privacy
  privacyPage: {
    title: "Privacy Policy",
    subtitle: "Information on the processing of personal data pursuant to EU Regulation 2016/679 (GDPR).",
  },
  cookiePage: {
    title: "Cookie Policy",
    subtitle: "Information about the use of cookies and any third-party content present on the website.",
  },

  // Stats 2025
  stats2025: {
    title: "2025 in numbers",
    subtitle: "AVPS activity in 2025.",
    kmTitle: "Kilometres driven",
    kmItems: [
      { label: "Secondary transport Agrate Brianza", value: "60,338" },
      { label: "Secondary transport Concorezzo (Nov-Dec)", value: "10,645" },
      { label: "Secondary transport Vimercate HQ", value: "56,863" },
      { label: "Logistics", value: "30,495" },
      { label: "After-hours care", value: "2,628" },
      { label: "Emergency", value: "245,948" },
    ],
    kmTotale: "406,917",
    missioniTitle: "Missions",
    missioniItems: [
      { label: "Token", value: "264" },
      { label: "Arcore", value: "1,785" },
      { label: "Agrate", value: "4,161" },
      { label: "Cornate", value: "669" },
      { label: "Monza", value: "1,927" },
      { label: "Vimercate", value: "4,937" },
      { label: "Secondary services Vimercate", value: "1,011" },
      { label: "Transits and recovery OK", value: "827" },
      { label: "Test tube service", value: "152" },
      { label: "TSS service Agrate", value: "1,462" },
      { label: "TSS service Concorezzo (Nov-Dec)", value: "856" },
    ],
    missioniTotale: "18,057",
    totaleLabel: "Total",
  },

  // Banner Lavora con noi (home)
  lavoraBanner: {
    title: "Work with us",
    badge: "Open position",
    text: "We're looking for an Ambulance Driver/Rescuer to join our team! Summer contract (June–September) with the possibility of permanent employment.",
    highlights: [
      "Rescue Operator certification",
      "Flexible shifts",
      "Ongoing training",
    ],
    cta: "Apply now",
  },

  // 404
  notFound: {
    title: "404",
    text: "Oops! Page not found",
    link: "Return to home",
  },
};
